import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as dms from '../../../../../../dms'
import type { Plugin } from '../plugin'

const SPACE_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.space.RESOURCE.payload,
)

const CANCELLATION_REASON = {
  noContext: 'No scenario context available',
  noLeafSpaceEntity: 'No leaf space entity available in the schema',
  noAssignedLabs: 'No labs assigned to the Lab Service',
} as const

/**
 * Relays a request for the current Lab Service to a self-contained
 * functionalizations request. This is the single place that resolves the
 * scenario context and the assigned lab record ids (from the collection list and
 * the space-dimension leaf entity), which the {@link executeLoad} transport then
 * fetches.
 */
export const loadForCurrentEntity = store.effects.create(
  (
    feature: Plugin['feature'],
    plugin: Plugin,
    schemaFeature: dms.schema.Feature,
  ) =>
    feature.actions.loadForCurrentEntity.relay(
      forwardTo =>
        rxjs
          .combineLatest([
            plugin.collection.plugins.cachePlugin.feature.selectors
              .getCachedEntities()
              .asObservable,
            plugin.collection.feature.selectors.context.asObservable,
            schemaFeature.selectors.schema.asObservable,
          ])
          .pipe(
            rxjs.take(1),
            rxjs.map(([labAssignments, context, schema]) => {
              if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
                return {
                  cancellation: { reason: CANCELLATION_REASON.noContext },
                }
              }

              const leafSpaceEntityId =
                schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.id
              if (!leafSpaceEntityId) {
                return {
                  cancellation: {
                    reason: CANCELLATION_REASON.noLeafSpaceEntity,
                  },
                }
              }

              const spaceRecordIds = labAssignments
                .filter(TypeChecker.isNotNullish.raw)
                .map(
                  labAssignment =>
                    labAssignment.spaceRecordLookup[leafSpaceEntityId]?.id,
                )
                .filter(TypeChecker.isNotNullish.raw)

              if (spaceRecordIds.length === 0) {
                return {
                  cancellation: { reason: CANCELLATION_REASON.noAssignedLabs },
                }
              }

              return forwardTo(feature.actions.load, {
                input: { context, spaceRecordIds },
                output: ({ response }) => response,
              })
            }),
          ),
      { strategy: 'switch' },
    ).asObservable,
)
